import Phaser from "phaser";
import { NineSliceButton, NINE_SLICE_BUTTON_EVENT, type ScrollableList } from "@yjsgames/phaser-ui";
import { FONT_FAMILY, PRIMARY_BUTTON_STYLE, TYPO } from "./uiTokens.js";
import { PALETTE, PALETTE_NUM } from "../themes/palette.js";
import { SHOP_BUTTON_HEIGHT, SHOP_HELP_SIZE, type ShopCardLayout } from "./shopLayout.js";

export interface CardObjects {
  readonly bg: Phaser.GameObjects.Rectangle;
  readonly icon: Phaser.GameObjects.Image;
  readonly desc: Phaser.GameObjects.Text;
  readonly button: NineSliceButton;
  readonly help?: NineSliceButton;
  readonly discountBadge?: Phaser.GameObjects.Text;
}

export interface ShopCardOptions {
  readonly discountLabel?: string;
}

const SHOP_DISCOUNT_BADGE_OUTSET = 6;

export function resolveShopDiscountBadgePlacement(c: ShopCardLayout): { x: number; y: number; originX: number } {
  return {
    x: c.bg.x + c.bg.width + SHOP_DISCOUNT_BADGE_OUTSET,
    y: c.bg.y - SHOP_DISCOUNT_BADGE_OUTSET,
    originX: 1,
  };
}

export function resolveShopHelpPosition(c: ShopCardLayout, hasDiscountBadge: boolean): { x: number; y: number } {
  if (!hasDiscountBadge) return c.help;
  return {
    x: c.bg.x + SHOP_DISCOUNT_BADGE_OUTSET + SHOP_HELP_SIZE / 2,
    y: c.bg.y + SHOP_DISCOUNT_BADGE_OUTSET + SHOP_HELP_SIZE / 2,
  };
}

export function createShopCard(
  scene: Phaser.Scene,
  scrollList: ScrollableList,
  c: ShopCardLayout,
  productIconKey: string,
  descText: string,
  costIconKey: string,
  onClick: () => void,
  onHelp?: () => void,
  options: ShopCardOptions = {},
): CardObjects {
  const bg = scene.add
    .rectangle(0, 0, c.bg.width, c.bg.height, PALETTE_NUM.cellBg, 0.88)
    .setOrigin(0, 0)
    .setStrokeStyle(1, PALETTE_NUM.gridPathGlow, 0.28)
    .setDepth(1);
  const icon = scene.add.image(0, 0, productIconKey).setDisplaySize(30, 30).setDepth(2);
  const desc = scene.add
    .text(0, 0, descText, {
      fontFamily: FONT_FAMILY,
      fontSize: TYPO.tiny,
      color: PALETTE.textPrimary,
      align: "center",
    })
    .setOrigin(0.5, 0)
    .setWordWrapWidth(c.desc.wrapWidth)
    .setDepth(2);
  const button = new NineSliceButton(scene, 0, 0, {
    style: PRIMARY_BUTTON_STYLE,
    label: "",
    widthOverride: c.button.width,
    heightOverride: SHOP_BUTTON_HEIGHT,
    iconKey: costIconKey,
    iconSize: 12,
  });
  button.on(NINE_SLICE_BUTTON_EVENT.Click, onClick);
  button.setDepth(2);
  scrollList.addContent(bg, c.bg.x, c.bg.y);
  scrollList.addContent(icon, c.icon.x, c.icon.y);
  scrollList.addContent(desc, c.desc.x, c.desc.y);
  scrollList.addContent(button, c.button.x, c.button.y);
  let help: NineSliceButton | undefined;
  if (onHelp) {
    const helpPos = resolveShopHelpPosition(c, Boolean(options.discountLabel));
    help = new NineSliceButton(scene, 0, 0, {
      style: PRIMARY_BUTTON_STYLE,
      label: "?",
      widthOverride: SHOP_HELP_SIZE,
      heightOverride: SHOP_HELP_SIZE,
    });
    help.on(NINE_SLICE_BUTTON_EVENT.Click, onHelp);
    help.setDepth(3);
    scrollList.addContent(help, helpPos.x, helpPos.y);
  }
  let discountBadge: Phaser.GameObjects.Text | undefined;
  if (options.discountLabel) {
    const badgePadX = 5;
    const badgePadY = 2;
    const badge = resolveShopDiscountBadgePlacement(c);
    discountBadge = scene.add
      .text(0, 0, options.discountLabel, {
        fontFamily: FONT_FAMILY,
        fontSize: TYPO.micro,
        color: PALETTE.textBright,
        backgroundColor: PALETTE.signalDanger,
        padding: { x: badgePadX, y: badgePadY },
        fontStyle: "bold",
      })
      .setOrigin(badge.originX, 0)
      .setDepth(3);
    scrollList.addContent(discountBadge, badge.x, badge.y);
  }
  return { bg, icon, desc, button, help, discountBadge };
}

export function setShopCardVisible(card: CardObjects, visible: boolean): void {
  [card.bg, card.icon, card.desc, card.button, card.help, card.discountBadge].forEach((obj) =>
    obj?.setVisible(visible),
  );
}

export function setShopCardDimmed(card: CardObjects, dimmed: boolean): void {
  card.bg.setAlpha(dimmed ? 0.46 : 1);
  card.icon.setAlpha(dimmed ? 0.36 : 1);
  card.desc.setAlpha(dimmed ? 0.58 : 1);
  card.button.setAlpha(dimmed ? 0.72 : 1);
  card.discountBadge?.setAlpha(dimmed ? 0.72 : 1);
}
